import { DroneIcon, NotificationsIcon } from '@assets/icons';
import ChatBubbleOnline from '@assets/icons/minor/ChatBubbleOnline';
import { ReportsIcon } from '@assets/icons/minor/ReportsIcon';
import { SubscriptionsIcon } from '@assets/icons/SubscriptionsIcon';
import explorerLogo from '@assets/images/common/cw_explorer_logo.svg';
import logoImage from '@assets/images/common/logo_horizontal.svg';
import { useNotificationsCenter } from '@contexts/NotificationsCenterContext';
import { useSystemLanguage } from '@contexts/SystemLanguageContext';
import useUnitSystem from '@contexts/UnitSystemContext';
import { TOPBAR_SELECTOR } from '@core/constants/layout';
import { SETTINGS_MENU_CLOSE_EVENT } from '@core/constants/settingsMenu';
import { SupportedEntitlements } from '@core/models/entitlements';
import { Organization } from '@core/models/organization';
import type { SettingsMenuOpenState } from '@core/models/settingsMenu';
import type { AiChatbotButtonMfeProps, AiChatbotMfeProps } from '@cw-elements/host-kit';
import {
  ProfileDropdownProps,
  SettingsMenuProps,
  TopbarNavigationMFEProps,
  TopbarNavigationSkeleton,
} from '@cw-elements/host-kit';
import { SelectedEntitiesModel } from '@cw-elements/mfe-setup/entities-hall/entities-hall.model';
import { loadRemoteModule } from '@cw-elements/mfe-setup/load';
import { ErrorBoundary } from '@datadog/browser-rum-react';
import { useAnomaliesByOrg } from '@hooks/queries/anomalyDetection/useAnomaliesByOrg';
import { useOrganization } from '@hooks/queries/useOrganization';
import { useProperty } from '@hooks/queries/useProperty';
import { useReserveWorkspacePanel } from '@hooks/UI/useReserveWorkspacePanel';
import { useWorkspaceReflow } from '@hooks/UI/useWorkspaceReflow';
import { useEntitlements } from '@hooks/useEntitlements';
import { useImageryFlags } from '@hooks/useImageryFlags';
import { useUnits } from '@hooks/useUnits';
import { logoutThunk } from '@redux/auth/thunks';
import { farmAnomalyDataInformation } from '@screens/Organizations/HallOfEntitiesMfe.utils';
import { routePaths } from '@screens/routes';
import { hallOfEntitiesStore } from '@state/hallOfEntities/hallOfEntities.store';
import { DB_NAME } from '@utils/indexeddb.utils';
import { getLanguageTag } from '@utils/translations';
import { UnitSystem, UnitSystems } from '@utils/unitUtils';
import localforage from 'localforage';
import {
  Dispatch,
  SetStateAction,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useMatch, useNavigate, useParams } from 'react-router';
import { Amplitude } from 'src/amplitude';

const SETTINGS_PANEL_KEY = 'settings-menu';

const topbarNavigationSkeletonFeatureConfig = {
  hasSettingsMenu: undefined,
  hasProfileDropdown: undefined,
  hasDeckOfApps: undefined,
};

const ErrorFallback = () => (
  <TopbarNavigationSkeleton featureConfig={topbarNavigationSkeletonFeatureConfig} />
);
const TopbarNavigation = lazy(() =>
  loadRemoteModule<TopbarNavigationMFEProps>('topbar-navigation/index')
);
const AiChatbotButton = lazy(() =>
  loadRemoteModule<AiChatbotButtonMfeProps>('ai-chatbot/chatbot-button')
);
const AiChatbot = lazy(() => loadRemoteModule<AiChatbotMfeProps>('ai-chatbot/index'));

const AiChatbotUnavailableSignal = ({ onUnavailable }: { onUnavailable: () => void }) => {
  useEffect(() => {
    onUnavailable();
  }, [onUnavailable]);
  return null;
};

interface TopbarNavigationProps {
  reportsDrawerVisible?: boolean;
  setReportsDrawerVisible: Dispatch<SetStateAction<boolean>>;
  setDroneDrawerVisible: Dispatch<SetStateAction<boolean>>;
  setHelpDrawerVisible: Dispatch<SetStateAction<boolean>>;
  setReportNotifications: Dispatch<SetStateAction<number>>;
}
export function TopbarNavigationMFE({
  reportsDrawerVisible,
  setReportsDrawerVisible,
  setDroneDrawerVisible,
  setHelpDrawerVisible,
  setReportNotifications,
}: Readonly<TopbarNavigationProps>) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { organizationId } = useParams();
  const entitlements = useEntitlements();
  const [unit, setUnit] = useUnitSystem();
  const { organization } = useOrganization();
  const { property } = useProperty();
  const [language, handleChangeLanguage, availableLanguages] = useSystemLanguage();
  const {
    popoverVisible: notificationsPopoverVisible,
    setPopoverVisible: setNotificationPopoverVisible,
  } = useNotificationsCenter();
  const { selectedEntities, setSelectedEntities } = hallOfEntitiesStore();
  const { FlagNewReportsDrawer } = useImageryFlags(organization);

  const workspaceId = organization?.workspace_id ?? selectedEntities.workspace?.id;
  const orgId = organizationId ?? organization?.id ?? selectedEntities.organization?.id;
  const propertyId = property?.id ?? selectedEntities.farm?.id;
  const [topbarOrganization, setTopbarOrganization] = useState<string | undefined>(orgId);
  const { data: anomaliesByOrg, isLoading } = useAnomaliesByOrg({
    org: topbarOrganization ? ({ id: topbarOrganization } as Organization) : organization,
  });

  const { longTranslationKey, area } = useUnits();

  const showProperty = useMatch({ path: routePaths.view, end: false });
  const hasAccessToDrone = entitlements.has(SupportedEntitlements.SOURCE_DRONE);
  const hasWeeklyReportsEntitlement = entitlements.has(
    SupportedEntitlements.WEEKLY_FIELD_INSIGHTS_REPORT
  );
  // Mirror the property dropdown / map rule: only surface anomaly indicators when the org has
  // the AD entitlement. Without this gate the topbar farm dropdown showed AD badges for orgs
  // without the entitlement (CAD-3051).
  const hasAnomalyDetectionEnabled = entitlements.has(SupportedEntitlements.ANOMALY_DETECTION);
  const isOnExplorer = globalThis.location.href.includes(
    import.meta.env.VITE_APP_CW_EXPLORER_DOMAIN
  );

  const availableLanguagesKeys = Object.keys(availableLanguages).map(
    (unitKey) => availableLanguages[unitKey]
  );

  const availableUnitSystemsKeys = Object.keys(UnitSystems).map(
    (unitKey) => UnitSystems[unitKey].id
  );

  const onSelectEntity = useCallback(
    (entity: SelectedEntitiesModel) => {
      setSelectedEntities(entity);
      if (entity.farm?.id && entity.organization?.id) {
        const url = `/app/organizations/${entity.organization.id}/properties/${entity.farm.id}`;
        navigate(url);
        return;
      }
      if (entity.organization?.id && entity.workspace?.id) {
        const url = `/app/organizations/workspace/${entity.workspace.id}/organization/${entity.organization.id}`;
        navigate(url);
      }
    },
    [setSelectedEntities, navigate]
  );

  const openSubscriptionsManagement = useCallback(() => {
    const url = `/app/organizations/${organization?.id}/subscriptions`;
    navigate(url);
  }, [organization?.id, navigate]);

  const handleLogout = useCallback(() => {
    dispatch(logoutThunk());

    localforage.dropInstance({
      name: DB_NAME,
    });
  }, [dispatch]);

  const handleChangeUnit = useCallback(
    (unit: UnitSystem | 'canada') => {
      const selectedUnit = unit === 'canada' ? 'imperial' : unit;
      setUnit(selectedUnit);
    },
    [setUnit]
  );

  const toggleHelpDrawer = useCallback(() => {
    setHelpDrawerVisible((prev) => !prev);
  }, [setHelpDrawerVisible]);

  const droneDrawerOn = useCallback(() => {
    if (hasAccessToDrone) {
      setDroneDrawerVisible(true);
      Amplitude.trackOpenDrawer();
    }
  }, [hasAccessToDrone, setDroneDrawerVisible]);

  const openNotificationsPopover = useCallback(() => {
    if (!notificationsPopoverVisible) {
      Amplitude.trackNotificationsOpenCenter();
    }
    setNotificationPopoverVisible(!notificationsPopoverVisible);
  }, [notificationsPopoverVisible, setNotificationPopoverVisible]);

  const [settingsPanelWidth, setSettingsPanelWidth] = useState(0);
  const reflowsWorkspace = useWorkspaceReflow();

  useReserveWorkspacePanel(SETTINGS_PANEL_KEY, reflowsWorkspace ? settingsPanelWidth : 0);

  const handleSettingsOpenChange = useCallback(({ open, width }: SettingsMenuOpenState) => {
    setSettingsPanelWidth(open ? width : 0);
  }, []);

  const [aiChatbotVisible, setAiChatbotVisible] = useState(false);
  const [aiChatbotOpened, setAiChatbotOpened] = useState(false);
  const [aiChatbotUnavailable, setAiChatbotUnavailable] = useState(false);

  const openAiChatbot = useCallback(() => {
    setAiChatbotOpened(true);
    setAiChatbotVisible(true);
  }, []);

  const closeAiChatbot = useCallback(() => {
    setAiChatbotVisible(false);
  }, []);

  const markAiChatbotUnavailable = useCallback(() => {
    setAiChatbotUnavailable(true);
    setAiChatbotVisible(false);
  }, []);

  const AiChatbotErrorFallback = useCallback(
    () => <AiChatbotUnavailableSignal onUnavailable={markAiChatbotUnavailable} />,
    [markAiChatbotUnavailable]
  );

  useEffect(() => {
    if (aiChatbotVisible) {
      Amplitude.trackAiChatbotOpen();
    }
  }, [aiChatbotVisible]);

  const onClickReportsIcon = useCallback(() => {
    if (FlagNewReportsDrawer || hasWeeklyReportsEntitlement) {
      setReportsDrawerVisible(!reportsDrawerVisible);
      setReportNotifications(0);
    }
  }, [
    reportsDrawerVisible,
    FlagNewReportsDrawer,
    hasWeeklyReportsEntitlement,
    setReportsDrawerVisible,
    setReportNotifications,
  ]);

  // Topbar MFE Product Settings
  const productSettings = useMemo(() => {
    const items = new Array<NonNullable<SettingsMenuProps['productSettings']>['items'][0]>();

    items.push(
      {
        key: 'subscriptions-management',
        icon: <SubscriptionsIcon />,
        label: t('header.settings.subscriptions_management'),
        tooltipText: t('header.settings.subscriptions_management'),
        onClick: () => {
          openSubscriptionsManagement();
          globalThis.dispatchEvent(new CustomEvent(SETTINGS_MENU_CLOSE_EVENT));
        },
      },
      {
        key: 'help',
        icon: <ChatBubbleOnline fill="#0071CD" />,
        label: t('header.settings.contact'),
        tooltipText: t('header.settings.contact_tooltip'),
        onClick: () => {
          toggleHelpDrawer();
          globalThis.dispatchEvent(new CustomEvent(SETTINGS_MENU_CLOSE_EVENT));
          Amplitude.trackHelpButton();
        },
      }
    );

    if (hasAccessToDrone) {
      items.push({
        key: 'drone',
        icon: <DroneIcon fill="#0071CD" />,
        label: t('header.settings.drone'),
        tooltipText: t('header.settings.drone_tooltip'),
        onClick: droneDrawerOn,
      });
    }

    return items;
  }, [t, hasAccessToDrone, openSubscriptionsManagement, droneDrawerOn, toggleHelpDrawer]);

  const additionalItemSettings = useMemo(() => {
    const items = new Array({
      tooltipText: t('header.additional_items.notifications'),
      icon: <NotificationsIcon size={18} id="topbar-notification-icon" />,
      onClick: () => {
        openNotificationsPopover();
      },
    });

    if (organization && (FlagNewReportsDrawer || hasWeeklyReportsEntitlement)) {
      items.push({
        tooltipText: t('header.additional_items.reports'),
        icon: <ReportsIcon width={16} height={16} id="topbar-report-icon" />,
        onClick: () => {
          onClickReportsIcon();
        },
      });
    }
    if (!aiChatbotUnavailable) {
      items.push({
        tooltipText: t('header.additional_items.ai_chatbot'),
        icon: (
          <span id="topbar-ai-chatbot-trigger">
            <ErrorBoundary fallback={AiChatbotErrorFallback}>
              <Suspense fallback={null}>
                <AiChatbotButton active={aiChatbotVisible} onClick={openAiChatbot} />
              </Suspense>
            </ErrorBoundary>
          </span>
        ),
        onClick: openAiChatbot,
      });
    }

    return items.reverse();
  }, [
    t,
    organization,
    FlagNewReportsDrawer,
    hasWeeklyReportsEntitlement,
    openNotificationsPopover,
    onClickReportsIcon,
    aiChatbotVisible,
    aiChatbotUnavailable,
    openAiChatbot,
    AiChatbotErrorFallback,
  ]);

  const topbarFeatureConfig = useMemo(() => {
    return {
      hasSettingsMenu: true,
      hasProfileDropdown: true,
      hasDeckOfApps: true,
      hideEntitiesDropdown: !showProperty,
    };
  }, [showProperty]);

  const topbarDefaultItems = useMemo(() => {
    return {
      selectedWorkspace: {
        value: workspaceId ?? '',
      },
      selectedOrganization: { value: orgId ?? '' },
      selectedFarm: { value: propertyId ?? '' },
    };
  }, [workspaceId, orgId, propertyId]);

  const topbarProductImage = useMemo(() => {
    return {
      redirectUrl: '/',
      imageSrc: isOnExplorer ? explorerLogo : logoImage,
    };
  }, [isOnExplorer]);

  const topbarOrganizationDropdown = useCallback(() => {
    return {
      onSelect: (org: { label?: string; value: string; workspaceId: string }) => {
        onSelectEntity({
          organization: { id: org.value, name: org.label ?? '' },
          workspace: { id: org.workspaceId, name: '' },
        });
      },
      goToEntity: () => {
        navigate(`/app/organizations/workspace/${organization?.workspace_id}`);
      },
    };
  }, [organization, onSelectEntity, navigate]);

  const topbarWorkspaceDropdown = useCallback(() => {
    return {
      onSelect: ({ value }: { value: string }) => {
        navigate(`/app/organizations/workspace/${value}`, {
          state: {
            stopRedirect: true,
          },
        });
      },
      goToEntity: () => {
        navigate(`/app/organizations`);
      },
    };
  }, [navigate]);

  const topbarFarmDropdown = useCallback(() => {
    return {
      hide: true,
      onSelect: (farm: { label?: string; value: string; orgId: string; workspaceId: string }) => {
        onSelectEntity({
          organization: { id: farm.orgId, name: '' },
          workspace: { id: farm.workspaceId, name: '' },
          farm: { id: farm.value, name: farm.label ?? '' },
        });
      },
      goToEntity: () => {
        navigate(
          `/app/organizations/workspace/${organization?.workspace_id}/organization/${organization?.id}`
        );
      },
    };
  }, [onSelectEntity, navigate, organization]);

  const topbarSettingsMenuConfig = useCallback(() => {
    return {
      headerSelector: TOPBAR_SELECTOR,
      onOpenChange: handleSettingsOpenChange,
      workspaceId: workspaceId,
      orgId: orgId,
      propertyId: propertyId,
      productSettings: {
        key: 'imagery',
        items: productSettings,
        title: isOnExplorer ? 'Explorer' : 'Imagery',
        tooltipText: isOnExplorer
          ? t('header.settings.explorer_group_settings_tooltip')
          : t('header.settings.group_settings_tooltip'),
      },
    };
  }, [workspaceId, orgId, propertyId, productSettings, isOnExplorer, t, handleSettingsOpenChange]);

  const topbarProfileDropdownConfig = useCallback(() => {
    return {
      availableUnitSystems: availableUnitSystemsKeys,
      defaultUnitSystem: unit.id,
      onUnitSystemChange: (currentUnit) => handleChangeUnit(currentUnit),
      availableLanguages: availableLanguagesKeys as ProfileDropdownProps['availableLanguages'],
      onLanguageChange: (currentLng) => {
        handleChangeLanguage(getLanguageTag(currentLng));
      },
      defaultLanguage: getLanguageTag(language),
    };
  }, [
    availableUnitSystemsKeys,
    unit,
    handleChangeUnit,
    availableLanguagesKeys,
    language,
    handleChangeLanguage,
  ]);

  const farmAdditionalData = useCallback(
    (farmId: string) => {
      if (!hasAnomalyDetectionEnabled) return null;
      return farmAnomalyDataInformation(
        farmId,
        anomaliesByOrg,
        isLoading,
        longTranslationKey,
        area,
        t
      );
    },
    [hasAnomalyDetectionEnabled, isLoading, area, anomaliesByOrg, longTranslationKey, t]
  );

  const topbarEntitiesHandleNavigation = useCallback(
    (selectedEntities: {
      workspaceId: string | null;
      divisionId: string | null;
      organizationId: string | null;
      farmId: string | null;
    }) => {
      setTopbarOrganization(selectedEntities.organizationId || undefined);
    },
    [setTopbarOrganization]
  );

  const topbarEntitiesAdditionalData = useCallback(
    (id: string, type: string) => {
      if (type === 'farm') {
        return farmAdditionalData(id);
      }
      return null;
    },
    [farmAdditionalData]
  );
  return (
    <>
      <ErrorBoundary fallback={ErrorFallback}>
        <Suspense
          fallback={
            <TopbarNavigationSkeleton
              featureConfig={topbarFeatureConfig}
              productImage={topbarProductImage}
            />
          }
        >
          <div>
            <TopbarNavigation
              userLanguage={language}
              onLogout={handleLogout}
              featureConfig={topbarFeatureConfig}
              workspaceDropdown={topbarWorkspaceDropdown}
              organizationDropdown={topbarOrganizationDropdown}
              farmDropdown={topbarFarmDropdown}
              defaultItems={topbarDefaultItems}
              productImage={topbarProductImage}
              settingsMenuConfig={topbarSettingsMenuConfig}
              profileDropdownConfig={topbarProfileDropdownConfig}
              additionalItems={() => additionalItemSettings}
              entitiesSelectorOnNavigate={topbarEntitiesHandleNavigation}
              entitiesSelectorAdditionalData={topbarEntitiesAdditionalData}
              appData={{
                name: 'Cropwise Explorer',
                id: import.meta.env.VITE_APP_IMAGERY_APPID,
              }}
            />
          </div>
        </Suspense>
      </ErrorBoundary>
      {aiChatbotOpened && !aiChatbotUnavailable && (
        <ErrorBoundary fallback={AiChatbotErrorFallback}>
          <Suspense fallback={null}>
            <AiChatbot
              userLanguage={language}
              isOpen={aiChatbotVisible}
              onClose={closeAiChatbot}
              entryPoint={isOnExplorer ? 'explorer' : 'imagery'}
              orgId={orgId ?? null}
              propertyId={propertyId ?? null}
              fieldId={null}
              workspaceId={workspaceId ?? null}
              headerSelector={TOPBAR_SELECTOR}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
}
